/** Minimum iframe height for embed error placeholders (e.g. broken Instagram). */
export const MIN_VALID_EMBED_HEIGHT = 280;

/** Delay before final DOM check after platform SDK runs. */
export const EMBED_CHECK_DELAY_MS = 3000;

/**
 * @param {string} url
 * @returns {boolean}
 */
export function isFacebookUrl(url) {
  return url.indexOf('facebook.com') > -1 || url.indexOf('fb.watch') > -1;
}

/**
 * @param {string} url
 * @returns {boolean}
 */
export function isYoutubeUrl(url) {
  return /youtube\.com|youtu\.be/i.test(url);
}

/**
 * @param {string} url
 * @returns {boolean}
 */
export function isTikTokUrl(url) {
  return url.indexOf('tiktok.com') > -1;
}

/**
 * @param {string} url
 * @returns {boolean}
 */
export function isTwitterUrl(url) {
  return url.indexOf('x.com') > -1 || url.indexOf('twitter.com') > -1;
}

/**
 * @param {string} url
 * @returns {boolean}
 */
export function isInstagramUrl(url) {
  return url.indexOf('instagram.com') > -1;
}

/**
 * @param {string} url
 * @returns {boolean}
 */
export function isInstagramMp4Url(url) {
  return url.indexOf('.mp4') > -1 && url.indexOf('Instagram') > -1;
}

/**
 * @param {string} url
 * @returns {boolean}
 */
export function isEmbeddablePlatformUrl(url) {
  return isFacebookUrl(url)
    || isYoutubeUrl(url)
    || isTikTokUrl(url)
    || isTwitterUrl(url)
    || isInstagramUrl(url)
    || isInstagramMp4Url(url);
}

/**
 * @param {string} url
 * @returns {boolean}
 */
export function isInstagramEmbeddablePostUrl(url) {
  return /instagram\.com\/(p|reel|tv)\//i.test(url);
}

/**
 * @param {string} url
 * @returns {boolean}
 */
export function isFacebookEmbeddablePostUrl(url) {
  return /facebook\.com\/(?:watch\/?\?|photo\.php|permalink\.php|story\.php|[^/?]+\/(?:posts|videos)\/|plugins\/post\.php)/i.test(url)
    || /fb\.watch\//i.test(url);
}

/**
 * @param {string} url
 * @returns {boolean}
 */
export function isTwitterEmbeddableTweetUrl(url) {
  return /(?:twitter\.com|x\.com)\/[^/?#]+\/status\/(\d+)/i.test(url);
}

/**
 * @param {string} url
 * @returns {boolean}
 */
export function isTikTokEmbeddableVideoUrl(url) {
  return /tiktok\.com\/@[^/]+\/video\/\d+/i.test(url)
    || /tiktok\.com\/t\/[A-Za-z0-9]+/i.test(url)
    || /vm\.tiktok\.com\//i.test(url);
}

/**
 * @param {string} url
 * @returns {string|null}
 */
export function extractYoutubeVideoId(url) {
  const patterns = [
    /(?:youtube\.com\/(?:watch\?.*v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/i,
    /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/i
  ];

  for (let i = 0; i < patterns.length; i += 1) {
    const match = url.match(patterns[i]);
    if (match && match[1]) {
      return match[1];
    }
  }

  const legacy = url.match(
    /(?:http[s]?:\/\/(?:www\.)?[a-z]{5,7}\.[a-z]{2,3}\/(?:watch\?v=)?)([a-zA-Z0-9_-]{11})(?:[?&]{1}t=)?([0-9]*)?/i
  );
  return legacy && legacy[1] ? legacy[1] : null;
}

/**
 * @param {string} url
 * @returns {string|null}
 */
export function extractTikTokVideoId(url) {
  const videoMatch = url.match(/\/video\/(\d+)/i);
  if (videoMatch && videoMatch[1]) {
    return videoMatch[1];
  }

  const shortMatch = url.match(/tiktok\.com\/t\/([A-Za-z0-9]+)/i);
  if (shortMatch && shortMatch[1]) {
    return shortMatch[1];
  }

  const index = url.lastIndexOf('/');
  if (index !== -1 && index < url.length - 1) {
    const segment = url.slice(index + 1).split('?')[0];
    if (/^\d+$/.test(segment)) {
      return segment;
    }
  }

  return null;
}

/**
 * @param {string} url
 * @returns {string|null}
 */
export function extractTwitterStatusId(url) {
  const match = url.match(/status\/(\d+)/i);
  return match && match[1] ? match[1] : null;
}

/**
 * Returns whether the URL shape is valid for the detected platform.
 * @param {string} url
 * @returns {boolean}
 */
export function isValidPlatformUrl(url) {
  if (isInstagramMp4Url(url)) {
    return /^https?:\/\/.+/i.test(url);
  }
  if (isYoutubeUrl(url)) {
    return Boolean(extractYoutubeVideoId(url));
  }
  if (isInstagramUrl(url)) {
    return isInstagramEmbeddablePostUrl(url);
  }
  if (isTikTokUrl(url)) {
    return isTikTokEmbeddableVideoUrl(url);
  }
  if (isTwitterUrl(url)) {
    return isTwitterEmbeddableTweetUrl(url);
  }
  if (isFacebookUrl(url)) {
    return isFacebookEmbeddablePostUrl(url);
  }
  return false;
}

/**
 * @param {string} oembedUrl
 * @returns {Promise<boolean>}
 */
export async function checkOembedAvailable(oembedUrl) {
  try {
    const response = await fetch(oembedUrl, { method: 'GET', mode: 'cors' });
    return response.ok;
  } catch (error) {
    return null;
  }
}

/**
 * Preflight embed availability via public oEmbed endpoints where supported.
 * @param {string} url
 * @returns {Promise<boolean|null>} true/false, or null if not checked
 */
export async function preflightEmbedUrl(url) {
  const encoded = encodeURIComponent(url);

  if (isYoutubeUrl(url)) {
    return checkOembedAvailable(`https://www.youtube.com/oembed?format=json&url=${encoded}`);
  }

  if (isTikTokUrl(url)) {
    return checkOembedAvailable(`https://www.tiktok.com/oembed?url=${encoded}`);
  }

  if (isTwitterUrl(url)) {
    return checkOembedAvailable(`https://publish.twitter.com/oembed?url=${encoded}`);
  }

  return null;
}

/**
 * @param {HTMLElement} root
 * @returns {number}
 */
function getEmbedIframeHeight(root) {
  const iframe = root.querySelector('iframe');
  if (!iframe) {
    return 0;
  }
  return iframe.offsetHeight
    || parseInt(iframe.getAttribute('height'), 10)
    || 0;
}

/**
 * @param {HTMLElement} root
 * @param {string} url
 * @returns {boolean}
 */
export function isRenderedEmbedValid(root, url) {
  if (!root) {
    return false;
  }

  if (isInstagramUrl(url)) {
    const blockquote = root.querySelector('blockquote.instagram-media');
    if (blockquote && blockquote.getAttribute('data-instgrm-payload-id')) {
      return true;
    }
    const height = getEmbedIframeHeight(root);
    if (height > 0) {
      return height >= MIN_VALID_EMBED_HEIGHT;
    }
    return false;
  }

  if (isTwitterUrl(url)) {
    const rendered = root.querySelector('.twitter-tweet-rendered');
    if (!rendered) {
      return false;
    }
    return Boolean(rendered.querySelector('iframe'));
  }

  if (isTikTokUrl(url)) {
    const iframe = root.querySelector('iframe[src*="tiktok"], .tiktok-embed iframe');
    return Boolean(iframe);
  }

  if (isFacebookUrl(url)) {
    const iframe = root.querySelector('.fb-post iframe, iframe[src*="facebook.com/plugins"]');
    return Boolean(iframe);
  }

  if (isYoutubeUrl(url)) {
    const videoId = extractYoutubeVideoId(url);
    const iframe = root.querySelector('iframe');
    if (!iframe || !videoId) {
      return false;
    }
    const src = iframe.getAttribute('src') || '';
    return src.indexOf(videoId) > -1;
  }

  return false;
}

/**
 * Builds a YouTube embed URL from an article URL.
 * @param {string} url
 * @returns {string|null}
 */
export function buildYoutubeEmbedUrl(url) {
  const videoId = extractYoutubeVideoId(url);
  if (!videoId) {
    return null;
  }

  const legacy = url.match(
    /(?:http[s]?:\/\/(?:www\.)?[a-z]{5,7}\.[a-z]{2,3}\/(?:watch\?v=)?)([a-zA-Z0-9_-]{11})(?:[?&]{1}t=)?([0-9]*)?/i
  );
  let startAt = '';
  if (legacy && legacy[2] && parseInt(legacy[2], 10) > 0) {
    startAt = parseInt(legacy[2], 10);
  }

  const timeMatch = url.match(/[?&]t=(\d+)/i);
  if (timeMatch && timeMatch[1] && parseInt(timeMatch[1], 10) > 0) {
    startAt = parseInt(timeMatch[1], 10);
  }

  return `https://www.youtube.com/embed/${videoId}${startAt ? `?start=${startAt}` : ''}`;
}
